/**
 * `RESTORE_MASTER_VERSION` step. Single-call wrapper over
 * `POST /api/v2/route/{routeId}/master/stops/restore`. The BE places stops on
 * the current master that aren't in the version at the top in existing order,
 * then lays the version stops underneath in stored order. With
 * `syncFuture:true` the BE also fans the restored master out to every future
 * daily route on the same `(routeId, dayOfWeek)`.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

import type { StepResult } from '../types'

export interface RestoreMasterVersionStepArgs {
  readonly routeId: RouteId
  readonly versionId: string
  readonly syncFuture: boolean
  readonly reassign: boolean
}

export type RestoreMasterVersionStepValue = { readonly stopCount: number }

export type RestoreMasterVersionStepResult = StepResult<'RESTORE_MASTER_VERSION', RestoreMasterVersionStepValue>

export const makeRestoreMasterVersionStep = (
  args: RestoreMasterVersionStepArgs,
  deps: Deps,
): PipelineStep<'RESTORE_MASTER_VERSION', RestoreMasterVersionStepResult> => {
  const stepId = `restore-master-version:${args.routeId}:${args.versionId}`
  return {
    id: stepId,
    phase: 'RESTORE_MASTER_VERSION',
    label: 'Failed to restore master sequence version',
    applyOrder: 200,
    run: async ctx => {
      try {
        const response = await deps
          .restoreMasterVersion({
            routeId: args.routeId,
            data: {
              versionId: args.versionId,
              syncFuture: args.syncFuture,
              reassign: args.reassign,
              versionContext: ctx.versionContext,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({
          phase: 'RESTORE_MASTER_VERSION',
          stepId,
          value: { stopCount: response?.stopCount ?? 0 },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'RESTORE_MASTER_VERSION',
          status,
          message,
        })
      }
    },
  }
}
